import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import {
  Button,
  ButtonProps,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Key, ReactNode, useState, type JSX } from "react";
import * as React from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";

type MetaT = {
  id: string;
};

export type MenuSelectOption<T extends Key = string, TMeta = unknown> = {
  value: T;
  label: ReactNode;
  meta?: TMeta;
  disabled?: boolean;
  tooltip?: ReactNode;
  icon?: IconDefinition;
};

export type MenuSelectProps<T extends Key = Key> = {
  onChange: (value: MenuSelectOption<T, MetaT>[]) => void;
  options: readonly MenuSelectOption<T, MetaT>[];
  value: MenuSelectOption<T, MetaT>[];
  placeholder?: string;
  id?: string;
  menuSx?: SxProps<Theme>;
};

type MenuItemButtonProps = Pick<ButtonProps, "children" | "onClick" | "startIcon"> & {
  selected: boolean;
  disabled?: boolean;
  removeLabel?: string;
};

const MenuItemButton = ({
  selected,
  disabled = false,
  children,
  onClick,
  removeLabel = "Remove",
  startIcon,
}: MenuItemButtonProps): JSX.Element => {
  const [hovered, setHovered] = useState(false);

  // Handle the hover behavior with JS to avoid applying hover state right after selection
  // i.e. hover state should be applied on the subsequent event after selection
  const handleMouseEnter = () => {
    if (selected) {
      setHovered(true);
    }
  };
  const handleMouseLeave = () => {
    if (selected) {
      setHovered(false);
    }
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHovered(false);
    onClick?.(event);
  };

  return (
    <Button
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      variant="contained"
      color="inherit"
      fullWidth
      startIcon={startIcon}
      disabled={disabled}
      sx={
        selected
          ? {
              border: (theme) => `1px solid ${theme.vars.palette.primary.main}`,
            }
          : {}
      }
    >
      {selected && hovered ? (
        <Stack direction="row" gap={1} alignItems="center">
          <FaIcon icon={faXmark} /> {removeLabel}
        </Stack>
      ) : (
        children
      )}
    </Button>
  );
};

export const MenuSelect = <T extends Key = Key>({
  id,
  menuSx,
  onChange,
  options,
  value,
  placeholder,
}: MenuSelectProps<T>): JSX.Element => {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const anchorEl = React.useRef<null | HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const handleClick = () => {
    setOpen(true);
  };

  const isSelected = (opt: MenuSelectOption<T, MetaT>): boolean =>
    !!value.find((val) => val.meta?.id === opt.meta?.id);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (option: MenuSelectOption<T, MetaT>) => {
    if (isSelected(option)) {
      onChange(value.filter((val) => val.meta?.id !== option.meta?.id));
    } else {
      onChange([...value, option]);
    }
    setOpen(false);
  };

  return (
    <div>
      <Stack gap={1} ref={anchorEl}>
        {value.map((val) => (
          <Tooltip
            key={val.meta?.id}
            title={val.tooltip || ""}
            placement={isMobile ? "bottom" : "right"}
            enterNextDelay={200}
          >
            <span>
              <MenuItemButton
                key={val.meta?.id}
                startIcon={val.icon && <FaIcon icon={val.icon} fontSize="small" />}
                disabled={val.disabled}
                onClick={() => handleSelect(val)}
                selected
              >
                {val.label}
              </MenuItemButton>
            </span>
          </Tooltip>
        ))}
        <Button
          variant="contained"
          color={value.length > 0 ? "secondary" : "primary"}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
        >
          {placeholder}
          <FaIcon icon={faAngleDown} />
        </Button>
      </Stack>
      <Menu
        id={id}
        anchorEl={anchorEl.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        variant="selectedMenu"
        PaperProps={{
          sx: {
            width: anchorEl.current?.offsetWidth,
          },
        }}
        sx={menuSx}
      >
        {options.map((opt) => {
          const selected = isSelected(opt);
          return (
            <Tooltip
              key={opt.meta?.id}
              title={opt.tooltip || ""}
              placement={isMobile ? "bottom" : "right"}
              enterNextDelay={200}
            >
              <span>
                <MenuItem
                  onClick={() => handleSelect(opt)}
                  disabled={opt.disabled}
                  sx={{
                    px: 1,
                    py: 0.5,
                    "&:hover": {
                      bgcolor: "unset",
                    },
                  }}
                >
                  <MenuItemButton
                    selected={selected}
                    startIcon={opt.icon && <FaIcon icon={opt.icon} fontSize="small" />}
                  >
                    {opt.label}
                  </MenuItemButton>
                </MenuItem>
              </span>
            </Tooltip>
          );
        })}
      </Menu>
    </div>
  );
};
